import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getApiUrl } from '@/lib/apiUrls';

/**
 * Is the signed-in customer VIP (src/constants/vip.js)? For their own VIP
 * badge (founder, 2026-10-25): Profile, the account menu, Home, Rewards and
 * their trip pages. Asked once per account per page load from
 * GET /api/me/vip, which answers yes or no for that account only. Staff
 * pages use useVipCustomers instead.
 */

let state = { userId: null, vip: false };
let pending = null;
let askingFor = null;
const listeners = new Set();

function publish(next) {
  state = next;
  listeners.forEach((fn) => fn(state));
}

async function load() {
  let asked = null;
  try {
    const { data } = await supabase.auth.getSession();
    const session = data?.session;
    if (!session?.access_token) {
      publish({ userId: null, vip: false });
      return;
    }
    if (state.userId === session.user?.id || askingFor === session.user?.id) return;
    // Nothing on screen needs the answer any more (the page closed): ask on the next mount.
    if (!listeners.size) {
      pending = null;
      return;
    }
    asked = session.user?.id || null;
    askingFor = asked;
    const res = await fetch(getApiUrl('me/vip'), {
      headers: { Authorization: `Bearer ${session.access_token}` },
      credentials: 'omit',
    });
    const body = await res.json().catch(() => ({}));
    if (askingFor !== asked) return; // signed out (or switched) meanwhile
    publish({ userId: session.user?.id || null, vip: Boolean(res.ok && body?.ok && body.vip) });
  } catch {
    // The badge is a nicety: without an answer, it simply doesn't show.
  } finally {
    if (asked && askingFor === asked) askingFor = null;
  }
}

let watchingAuth = false;
/** Signing in, out or into another account asks again. */
function watchAuth() {
  if (watchingAuth) return;
  watchingAuth = true;
  try {
    supabase.auth.onAuthStateChange?.((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        publish({ userId: null, vip: false });
        pending = null;
        askingFor = null;
      } else if (session.user?.id !== state.userId && session.user?.id !== askingFor) {
        pending = load();
      }
    });
  } catch {
    // No auth events (tests, native boot): the first read stands.
  }
}

/** true when the signed-in customer is VIP. */
export function useMyVip() {
  const [snapshot, setSnapshot] = useState(state);
  useEffect(() => {
    listeners.add(setSnapshot);
    if (!pending) pending = load();
    watchAuth();
    setSnapshot(state);
    return () => { listeners.delete(setSnapshot); };
  }, []);
  return snapshot.vip;
}

/** Tests only: forget the answer. */
export function resetMyVip() {
  state = { userId: null, vip: false };
  pending = null;
  askingFor = null;
}
